import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getHealth, getTenantHealth, getTenantDeepHealth } from '../Services/services';
import { useBranding } from '../Context/BrandingContext';

function Health() {
  const { branding } = useBranding();
  const [checks, setChecks] = useState({
    app: { status: true, label: 'Client Interface', message: 'Running' },
    clientApi: { status: null, label: 'Client API', message: 'Checking...' },
    azureTable: { status: null, label: 'Client Storage', message: 'Checking...' },
    tenantApi: { status: null, label: 'Tenant API', message: 'Checking...' },
    cosmosDb: { status: null, label: 'Tenant Storage', message: 'Checking...' },
  });
  const [lastChecked, setLastChecked] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const runHealthChecks = useCallback(async () => {
    setIsRefreshing(true);

    // Reset checks to loading state (except app which is always true)
    setChecks((prev) => ({
      ...prev,
      clientApi: { ...prev.clientApi, status: null, message: 'Checking...' },
      azureTable: { ...prev.azureTable, status: null, message: 'Checking...' },
      tenantApi: { ...prev.tenantApi, status: null, message: 'Checking...' },
      cosmosDb: { ...prev.cosmosDb, status: null, message: 'Checking...' },
    }));

    // Check 1: Client API + Azure Table Storage
    try {
      const healthResponse = await getHealth();
      const data = healthResponse.data;

      // Client API is working if we got a response
      const clientApiOk = data.status === 'ok' || data.status === 'degraded';
      
      setChecks((prev) => ({
        ...prev,
        clientApi: {
          ...prev.clientApi,
          status: clientApiOk,
          message: clientApiOk ? 'Connected' : 'Error',
        },
        azureTable: {
          ...prev.azureTable,
          status: data.checks?.azure_table_storage === true,
          message: data.checks?.azure_table_storage === true
            ? 'Connected'
            : data.checks?.azure_table_storage_error || 'Not configured',
        },
      }));
    } catch (error) {
      setChecks((prev) => ({
        ...prev,
        clientApi: {
          ...prev.clientApi,
          status: false,
          message: error.response?.data?.status || error.message || 'Connection failed',
        },
        azureTable: {
          ...prev.azureTable,
          status: false,
          message: 'Cannot check - Client API unavailable',
        },
      }));
    }

    // Check 2: Tenant API accessibility
    try {
      const tenantResponse = await getTenantHealth();
      const data = tenantResponse.data;

      setChecks((prev) => ({
        ...prev,
        tenantApi: {
          ...prev.tenantApi,
          status: data.status === 'ok',
          message: data.status === 'ok'
            ? `Connected (${data.latency_ms}ms)`
            : data.error || 'Error',
        },
      }));
    } catch (error) {
      setChecks((prev) => ({
        ...prev,
        tenantApi: {
          ...prev.tenantApi,
          status: false,
          message: error.response?.data?.error || error.message || 'Connection failed',
        },
      }));
    }

    // Check 3: Tenant API deep (Cosmos DB)
    try {
      const deepResponse = await getTenantDeepHealth();
      const data = deepResponse.data;

      const cosmosOk = data.tenant_api_deep?.checks?.cosmos_db === true;
      setChecks((prev) => ({
        ...prev,
        cosmosDb: {
          ...prev.cosmosDb,
          status: cosmosOk,
          message: cosmosOk
            ? `Connected (${data.latency_ms}ms)`
            : data.tenant_api_deep?.checks?.cosmos_db_error || 'Error',
        },
      }));
    } catch (error) {
      setChecks((prev) => ({
        ...prev,
        cosmosDb: {
          ...prev.cosmosDb,
          status: false,
          message: error.response?.data?.error || error.message || 'Connection failed',
        },
      }));
    }

    setLastChecked(new Date());
    setIsRefreshing(false);
  }, []);

  useEffect(() => {
    runHealthChecks();

    // Auto-refresh every 30 seconds
    const interval = setInterval(runHealthChecks, 30000);
    return () => clearInterval(interval);
  }, [runHealthChecks]);

  const getStatusIcon = (status) => {
    if (status === null) {
      return <span style={styles.iconLoading}>○</span>;
    }
    return status ? (
      <span style={styles.iconSuccess}>✓</span>
    ) : (
      <span style={styles.iconError}>✗</span>
    );
  };

  const getOverallStatus = () => {
    const statuses = Object.values(checks).map((c) => c.status);
    if (statuses.some((s) => s === null)) return 'checking';
    if (statuses.every((s) => s === true)) return 'healthy';
    return 'unhealthy';
  };

  const overallStatus = getOverallStatus();

  // Dynamic styles based on branding
  const dynamicStyles = {
    header: {
      ...styles.header,
      backgroundColor: branding.primaryColor,
    },
    homeButton: {
      ...styles.homeButton,
      color: branding.primaryColor,
    },
    refreshButton: {
      ...styles.refreshButton,
      backgroundColor: branding.primaryColor,
    },
  };

  return (
    <div style={styles.page}>
      {/* Header Banner */}
      <header style={dynamicStyles.header}>
        <div style={styles.headerContent}>
          <div style={styles.brand}>
            <svg style={styles.logo} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 16V14L13 9V3.5C13 2.67 12.33 2 11.5 2C10.67 2 10 2.67 10 3.5V9L2 14V16L10 13.5V19L8 20.5V22L11.5 21L15 22V20.5L13 19V13.5L21 16Z" fill="white" />
            </svg>
            <span style={styles.brandName}>{branding.companyName}</span>
          </div>
          <Link to="/" style={dynamicStyles.homeButton}>
            ← Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main style={styles.main}>
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h1 style={styles.title}>System Status</h1>
            <div
              style={{
                ...styles.statusBadge,
                backgroundColor:
                  overallStatus === 'healthy'
                    ? '#4caf50'
                    : overallStatus === 'checking'
                    ? '#ff9800'
                    : '#f44336',
              }}
            >
              {overallStatus === 'healthy'
                ? 'All Systems Operational'
                : overallStatus === 'checking'
                ? 'Checking...'
                : 'Issues Detected'}
            </div>
          </div>

          <div style={styles.checksContainer}>
            {Object.entries(checks).map(([key, check]) => (
              <div
                key={key}
                style={{
                  ...styles.checkRow,
                  backgroundColor:
                    check.status === null
                      ? '#fff8e1'
                      : check.status
                      ? '#e8f5e9'
                      : '#ffebee',
                }}
              >
                <div style={styles.checkIcon}>{getStatusIcon(check.status)}</div>
                <div style={styles.checkInfo}>
                  <div style={styles.checkLabel}>{check.label}</div>
                  <div
                    style={{
                      ...styles.checkMessage,
                      color:
                        check.status === null
                          ? '#ff9800'
                          : check.status
                          ? '#4caf50'
                          : '#f44336',
                    }}
                  >
                    {check.message}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={styles.cardFooter}>
            <div style={styles.lastChecked}>
              {lastChecked && (
                <span>Last checked: {lastChecked.toLocaleTimeString()}</span>
              )}
            </div>
            <button
              onClick={runHealthChecks}
              style={dynamicStyles.refreshButton}
              disabled={isRefreshing}
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <span style={styles.footerText}>
          © {new Date().getFullYear()} {branding.companyName}. All rights reserved.
        </span>
      </footer>
    </div>
  );
}

const styles = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  header: {
    padding: '0 24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1200px',
    margin: '0 auto',
    height: '64px',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    textDecoration: 'none',
  },
  logo: {
    width: '32px',
    height: '32px',
  },
  brandName: {
    fontSize: '22px',
    fontWeight: '700',
    color: 'white',
    letterSpacing: '-0.5px',
  },
  homeButton: {
    backgroundColor: 'white',
    padding: '10px 24px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    textDecoration: 'none',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  main: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    backgroundColor: '#f0f4f8',
    padding: '40px 24px',
  },
  card: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    width: '100%',
    maxWidth: '500px',
  },
  cardHeader: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  title: {
    margin: '0 0 16px 0',
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a1a2e',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '8px 16px',
    borderRadius: '20px',
    color: 'white',
    fontSize: '14px',
    fontWeight: '600',
  },
  checksContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  checkRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    borderRadius: '8px',
    transition: 'background-color 0.3s',
  },
  checkIcon: {
    marginRight: '16px',
    fontSize: '24px',
    width: '32px',
    textAlign: 'center',
  },
  iconSuccess: {
    color: '#4caf50',
    fontWeight: 'bold',
  },
  iconError: {
    color: '#f44336',
    fontWeight: 'bold',
  },
  iconLoading: {
    color: '#ff9800',
  },
  checkInfo: {
    flex: 1,
  },
  checkLabel: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '4px',
  },
  checkMessage: {
    fontSize: '14px',
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '24px',
    paddingTop: '16px',
    borderTop: '1px solid #eee',
  },
  lastChecked: {
    color: '#666',
    fontSize: '14px',
  },
  refreshButton: {
    padding: '10px 20px',
    backgroundColor: '#0066cc',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },
  footer: {
    backgroundColor: 'white',
    padding: '20px 24px',
    textAlign: 'center',
    borderTop: '1px solid #e2e8f0',
  },
  footerText: {
    fontSize: '13px',
    color: '#718096',
  },
};

export default Health;
